import {
  MemoryInfo,
  PerformanceStats,
  CONSTANTS
} from '../types';

/**
 * 内存管理和性能优化工具类
 */
export class PerformanceUtils {
  private static memoryCheckInterval: NodeJS.Timeout | null = null;
  private static memoryWarningCallbacks: Array<(info: MemoryInfo) => void> = [];
  private static performanceStats: PerformanceStats = {
    totalProcessedFiles: 0,
    totalProcessingTime: 0,
    averageProcessingTime: 0,
    memoryPeakUsage: 0,
    errorRate: 0,
    cancelRate: 0
  };

  /**
   * 获取内存使用信息
   */
  static getMemoryInfo(): MemoryInfo | null {
    if (!('memory' in performance)) {
      return null;
    }

    const memory = (performance as any).memory;
    const memoryUsagePercentage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      memoryUsagePercentage
    };
  }

  /**
   * 检查内存使用是否超出阈值
   */
  static checkMemoryThreshold(): {
    isWarning: boolean;
    isCritical: boolean;
    memoryInfo: MemoryInfo | null;
  } {
    const memoryInfo = this.getMemoryInfo();

    if (!memoryInfo) {
      return {
        isWarning: false,
        isCritical: false,
        memoryInfo: null
      };
    }

    const { memoryUsagePercentage } = memoryInfo;

    return {
      isWarning: memoryUsagePercentage > CONSTANTS.MEMORY_WARNING_THRESHOLD,
      isCritical: memoryUsagePercentage > CONSTANTS.MEMORY_CRITICAL_THRESHOLD,
      memoryInfo
    };
  }

  /**
   * 开始内存监控
   */
  static startMemoryMonitoring(interval = 5000): void {
    if (this.memoryCheckInterval) {
      this.stopMemoryMonitoring();
    }

    this.memoryCheckInterval = setInterval(() => {
      const { isWarning, isCritical, memoryInfo } = this.checkMemoryThreshold();

      if (memoryInfo) {
        // 更新峰值使用记录
        if (memoryInfo.usedJSHeapSize > this.performanceStats.memoryPeakUsage) {
          this.performanceStats.memoryPeakUsage = memoryInfo.usedJSHeapSize;
        }

        // 触发警告回调
        if (isWarning || isCritical) {
          this.memoryWarningCallbacks.forEach(callback => {
            try {
              callback(memoryInfo);
            } catch (error) {
              console.warn('内存警告回调执行失败:', error);
            }
          });
        }

        // 在内存严重不足时强制垃圾回收
        if (isCritical) {
          this.forceGarbageCollection();
        }
      }
    }, interval);
  }

  /**
   * 停止内存监控
   */
  static stopMemoryMonitoring(): void {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
  }

  /**
   * 添加内存警告回调
   */
  static addMemoryWarningCallback(callback: (info: MemoryInfo) => void): () => void {
    this.memoryWarningCallbacks.push(callback);

    // 返回移除回调的函数
    return () => {
      const index = this.memoryWarningCallbacks.indexOf(callback);
      if (index > -1) {
        this.memoryWarningCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * 强制垃圾回收（如果支持）
   */
  static forceGarbageCollection(): void {
    if ('gc' in window && typeof (window as any).gc === 'function') {
      try {
        (window as any).gc();
      } catch (error) {
        console.warn('强制垃圾回收失败:', error);
      }
    }
  }

  /**
   * 清理URL对象
   */
  static cleanupObjectUrls(urls: string[]): void {
    urls.forEach(url => {
      if (url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.warn('清理URL失败:', url, error);
        }
      }
    });
  }

  /**
   * 延迟执行，让出主线程
   */
  static async delay(ms = 0): Promise<void> {
    return new Promise(resolve => {
      if (ms > 0) {
        setTimeout(resolve, ms);
      } else {
        // 使用MessageChannel实现0ms延迟，让出主线程
        const channel = new MessageChannel();
        channel.port2.onmessage = () => resolve();
        channel.port1.postMessage(null);
      }
    });
  }

  /**
   * 分批处理数据，避免阻塞UI
   */
  static async processBatchWithDelay<T, R>(
    items: T[],
    processor: (item: T, index: number) => Promise<R> | R,
    batchSize = 5,
    delayMs = 10
  ): Promise<R[]> {
    const results: R[] = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);

      // 处理当前批次
      const batchPromises = batch.map((item, index) =>
        Promise.resolve(processor(item, i + index))
      );

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // 在批次间延迟，让出主线程
      if (i + batchSize < items.length) {
        await this.delay(delayMs);
      }
    }

    return results;
  }

  /**
   * 测量函数执行时间
   */
  static async measurePerformance<T>(
    name: string,
    fn: () => Promise<T> | T
  ): Promise<{ result: T; duration: number }> {
    const startTime = performance.now();

    try {
      const result = await Promise.resolve(fn());
      const duration = performance.now() - startTime;

      console.log(`性能测量 [${name}]: ${duration.toFixed(2)}ms`);

      return { result, duration };
    } catch (error) {
      const duration = performance.now() - startTime;
      console.error(`性能测量 [${name}] 失败: ${duration.toFixed(2)}ms`, error);
      throw error;
    }
  }

  /**
   * 更新性能统计信息
   */
  static updatePerformanceStats(stats: Partial<PerformanceStats>): void {
    Object.assign(this.performanceStats, stats);

    // 重新计算平均值
    if (this.performanceStats.totalProcessedFiles > 0) {
      this.performanceStats.averageProcessingTime =
        this.performanceStats.totalProcessingTime / this.performanceStats.totalProcessedFiles;
    }
  }

  /**
   * 获取性能统计信息
   */
  static getPerformanceStats(): PerformanceStats {
    return { ...this.performanceStats };
  }

  /**
   * 重置性能统计信息
   */
  static resetPerformanceStats(): void {
    this.performanceStats = {
      totalProcessedFiles: 0,
      totalProcessingTime: 0,
      averageProcessingTime: 0,
      memoryPeakUsage: 0,
      errorRate: 0,
      cancelRate: 0
    };
  }

  /**
   * 获取推荐的批次大小
   */
  static getRecommendedBatchSize(): number {
    const memoryInfo = this.getMemoryInfo();

    if (!memoryInfo) {
      return CONSTANTS.BATCH_PROCESSING_SIZE;
    }

    const { memoryUsagePercentage } = memoryInfo;

    // 根据内存使用情况动态调整批次大小
    if (memoryUsagePercentage > 0.8) {
      return 2; // 高内存使用时减少批次大小
    } else if (memoryUsagePercentage > 0.6) {
      return 3;
    } else if (memoryUsagePercentage > 0.4) {
      return 5;
    } else {
      return 8; // 内存充足时可以增加批次大小
    }
  }

  /**
   * 检查设备性能能力
   */
  static assessDeviceCapability(): {
    level: 'low' | 'medium' | 'high';
    recommendedMaxFiles: number;
    recommendedBatchSize: number;
  } {
    let score = 0;

    // CPU核心数评分
    const cores = navigator.hardwareConcurrency || 4;
    score += cores >= 8 ? 3 : cores >= 4 ? 2 : 1;

    // 内存评分
    const memoryInfo = this.getMemoryInfo();
    if (memoryInfo) {
      const memoryGB = memoryInfo.jsHeapSizeLimit / (1024 * 1024 * 1024);
      score += memoryGB >= 4 ? 3 : memoryGB >= 2 ? 2 : 1;
    } else {
      score += 2; // 默认中等分数
    }

    // 用户代理评分（简单检测）
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Mobile') || userAgent.includes('Android')) {
      score -= 1; // 移动设备减分
    }

    // 根据得分确定设备能力
    const level = score >= 5 ? 'high' : score >= 3 ? 'medium' : 'low';

    const config = {
      low: { recommendedMaxFiles: 20, recommendedBatchSize: 2 },
      medium: { recommendedMaxFiles: 50, recommendedBatchSize: 5 },
      high: { recommendedMaxFiles: 100, recommendedBatchSize: 8 }
    };

    return {
      level,
      ...config[level]
    };
  }

  /**
   * 创建防抖函数
   */
  static debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;

    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * 创建节流函数
   */
  static throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;

    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * 格式化性能时间
   */
  static formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms.toFixed(0)}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0);
      return `${minutes}m${seconds}s`;
    }
  }

  /**
   * 格式化内存大小
   */
  static formatMemorySize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}